import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contentProblems, type AdminContentReply, type WorldContent } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { api } from '../../lib/api';

// The game's content in the admin: the published version, the draft, and the copy being edited here. Edits
// stay on this screen until "Save draft"; the checks run as you type, and again on the server.
export function useContent() {
  const [reply, setReply] = useState<AdminContentReply | null>(null);
  const [working, setWorking] = useState<WorldContent | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  // the copies before each change, newest last (Undo steps back one change at a time)
  const steps = useRef<WorldContent[]>([]);
  const [stepCount, setStepCount] = useState(0);
  const accept = useCallback((next: AdminContentReply) => {
    steps.current = [];
    setStepCount(0);
    setReply(next);
    const copy = structuredClone(next.draft?.content ?? next.published.content);
    setWorking(copy);
    setSaved(JSON.stringify(copy));
  }, []);
  const run = useCallback(
    async (work: () => Promise<AdminContentReply>) => {
      setBusy(true);
      setFailure(null);
      try {
        accept(await work());
        return true;
      } catch (problem) {
        setFailure(describeError(problem));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [accept],
  );
  useEffect(() => {
    void run(() => api.admin.content());
  }, [run]);
  const dirty = working !== null && JSON.stringify(working) !== saved;
  // leaving with unsaved changes asks first
  useEffect(() => {
    if (!dirty) return;
    const stay = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', stay);
    return () => window.removeEventListener('beforeunload', stay);
  }, [dirty]);
  const problems = useMemo(() => (working ? contentProblems(working) : []), [working]);
  // change the working copy (a place, an activity): the function gets a copy it may change
  // (the latest copy is kept in a ref too, so a change never runs inside React's state updater twice)
  const latest = useRef<WorldContent | null>(null);
  latest.current = working;
  const edit = useCallback((change: (content: WorldContent) => void) => {
    const current = latest.current;
    if (!current) return;
    const next = structuredClone(current);
    change(next);
    steps.current = [...steps.current, current].slice(-60);
    latest.current = next;
    setStepCount(steps.current.length);
    setWorking(next);
  }, []);
  const stepBack = useCallback(() => {
    const previous = steps.current.at(-1);
    if (!previous) return;
    steps.current = steps.current.slice(0, -1);
    setStepCount(steps.current.length);
    setWorking(previous);
  }, []);
  return {
    reply,
    working,
    dirty,
    problems,
    failure,
    busy,
    edit,
    fail: (problem: unknown) => setFailure(describeError(problem)),
    reload: () => run(() => api.admin.content()),
    saveDraft: (note?: string) =>
      working
        ? run(() => api.admin.saveContentDraft({ content: working, ...(note ? { note } : {}) }))
        : Promise.resolve(false),
    discardDraft: () => run(() => api.admin.discardContentDraft()),
    // what is on screen goes out: saved as the draft first if it changed
    publish: async (note?: string) => {
      if (dirty && working && !(await run(() => api.admin.saveContentDraft({ content: working })))) return false;
      return run(() => api.admin.publishContent(note));
    },
    restore: (versionId: string) => run(() => api.admin.restoreContent(versionId)),
    undo: () => {
      if (saved) setWorking(JSON.parse(saved) as WorldContent);
      steps.current = [];
      setStepCount(0);
    },
    // one change back (the designer's Undo)
    stepBack,
    canStepBack: stepCount > 0,
  };
}
export type ContentEditor = ReturnType<typeof useContent>;
